import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { APPROVAL_TONE, FAMILY_LABEL, approvalSentence, compsSentence, lotSentence, pct, residualText, sizeSentence, usd } from '../lib/feasibilityLabels';
import type { FeasibilityRow } from '../types/feasibility';

/**
 * FZ4 - "What could be built here" (spec section 10). It prints the STORED answer and never re-derives a number;
 * every figure says what it rests on. ⛔ A refusal is shown, never an empty panel.
 */
export default function WhatCouldBeBuilt({ propertyId }: { propertyId: string }) {
  const [rows, setRows] = useState<FeasibilityRow[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setRows(null);
    setError('');
    api.getPropertyFeasibility(propertyId)
      .then((r) => { if (live) setRows(r.rows); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : 'Could not read what could be built here'); });
    return () => { live = false; };
  }, [propertyId]);

  const shell = (body: React.ReactNode) => (
    <section aria-labelledby="wcbb-title" className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="wcbb-title" className="font-semibold text-gray-900">What could be built here</h3>
        <span className="text-xs text-gray-500">An estimate from the city's own documents - confirm with Planning</span>
      </div>
      {body}
    </section>
  );

  if (error) return shell(<p role="alert" className="mt-2 text-sm text-red-700">{error}</p>);
  if (rows === null) return shell(<p className="mt-2 text-sm text-gray-500">Loading...</p>);
  if (rows.length === 0) {
    return shell(<p className="mt-2 text-sm text-gray-600">Not worked out for this property yet - it is scored once its city has been researched.</p>);
  }
  const first = rows[0];
  const zoneLine = first.zone_source
    ? `Zone ${first.zone_code} - ${first.zone_jurisdiction}'s own zoning map, read ${first.zone_source_date}`
    : 'Zone not looked up yet - its zone has not been read from the city or county map';
  const lot = lotSentence(first);
  return shell(
    <>
      <p className="mt-1 text-sm text-gray-700">{zoneLine}</p>
      {lot && <p className="text-sm text-gray-700">{lot}</p>}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {rows.map((r) => <FamilyCard key={r.use_family} row={r} />)}
      </div>
    </>,
  );
}

function FamilyCard({ row }: { row: FeasibilityRow }) {
  const building = compsSentence(row.comps, 'sq ft');
  const land = compsSentence(row.land_comps, 'acre');
  return (
    <div className="rounded-md border border-gray-100 p-3" data-testid={`family-${row.use_family}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-900">{FAMILY_LABEL[row.use_family]}</span>
        <span className={`rounded px-2 py-0.5 text-xs ring-1 ${APPROVAL_TONE[row.approval]}`}>{approvalSentence(row)}</span>
      </div>
      {row.approval !== 'no_zone' && <p className="mt-2 text-sm text-gray-700">{sizeSentence(row)}</p>}
      <dl className="mt-2 space-y-1 text-sm">
        <div>
          <dt className="inline text-gray-500">What a developer could pay: </dt>
          <dd className="inline font-medium">{residualText(row)}</dd>
          {building && <span className="block text-xs text-gray-500">{building}</span>}
        </div>
        <div>
          <dt className="inline text-gray-500">What similar land sold for: </dt>
          <dd className="inline font-medium">
            {row.land_comp_value !== null ? usd(row.land_comp_value) : `not enough land sales nearby (found ${row.land_comps?.found ?? 0}) - import CoStar land comps`}
          </dd>
          {land && <span className="block text-xs text-gray-500">{land}</span>}
        </div>
        {row.listed && (
          <div>
            <dt className="inline text-gray-500">Asking: </dt>
            <dd className="inline">{usd(row.asking_price)}{row.asking_note ? ` (${row.asking_note})` : ''}</dd>
          </div>
        )}
        {row.upside !== null && (
          <div>
            <dt className="inline text-gray-500">Upside: </dt>
            <dd className={`inline font-semibold ${row.upside >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{`${usd(row.upside)} (${pct(row.upside_share)})`}</dd>
          </div>
        )}
      </dl>
      {row.disagreement && <p className="mt-1 text-xs text-amber-700">The two values disagree by more than the threshold - check both before quoting either.</p>}
      {row.assessed_land_value !== null && (
        <p className="mt-1 text-xs text-gray-500">{`Assessed land value (not market value): ${usd(row.assessed_land_value)}`}</p>
      )}
      {row.reasons.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-gray-600">{row.reasons.map((x) => <li key={x}>{x}</li>)}</ul>
      )}
    </div>
  );
}
